import { TypeManagerDecorator } from "../../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject,
} from "@angular/core";
import { S25Util } from "../../../util/s25-util";
import { IngestionPoint, DataIngestionService } from "../data.ingestion.service";
import {
    INGESTION_CATEGORIES,
    INGESTION_POINT_FORM_USE_KEYS,
    INGESTION_POINT_FORM_USES,
    IngestionPointCategory,
    type IngestionPointFormUseKey,
} from "../data.ingestion.const";
import {
    buildWorkflowNameById,
    filterIngestionPointsForList,
    formatFormUseColumnText,
    getDirectWorkflowDisplayName,
} from "./ingestion.admin.list.util";
import { WorkflowService } from "../../workflow/service/workflow.service";
import { StateService } from "../../../services/state.service";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { Table } from "../../s25-table/Table";
import Cell = Table.Cell;
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { UserprefService } from "../../../services/userpref.service";
import { jSith } from "../../../util/jquery-replacement";
import { AlertService } from "../../../services/alert.service";
import { PreferenceService } from "../../../services/preference.service";
import { INGESTION_SECURITY_SERVICE } from "../ingestion.security.service";
import { RoleBasedIngestionSecurityService } from "../role.based.ingestion.security.service";
import { copyIngestionForm } from "../ingestion.copy.form.util";
import { canCopyIngestionPoint, loadCopyableIngestionPointIds } from "../ingestion.copyable.forms.util";
import { onSeFormsActivated } from "./ingestion.admin.navigation";
import { loadSavedIngestionFormPreview } from "./ingestion.admin.preview.util";
import { ModalService } from "../../modal/modal.service";
import { ItemService } from "../../../util/items/item.service";

@TypeManagerDecorator("s25-ng-ingestion-admin")
@Component({
    selector: "s25-ng-ingestion-admin",
    providers: [
        // Provide default role-based security service
        // Projects can override by providing their own IngestionSecurityService via INGESTION_SECURITY_SERVICE token
        { provide: INGESTION_SECURITY_SERVICE, useClass: RoleBasedIngestionSecurityService },
    ],
    template: `
        @if (showAiBuilder) {
            <div class="c-margin-bottom--single">
                <h2 class="ai-assisted-heading">
                    <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" class="ai-sparkles-adjacent" />
                    AI Form + Workflow Builder
                </h2>
                <button (click)="closeAiBuilder()" class="aw-button aw-button--outline">← Back to Forms List</button>
            </div>
            <s25-ng-ai-form-workflow-builder (exit)="closeAiBuilder()"></s25-ng-ai-form-workflow-builder>
        }

        @if (aiEditForm) {
            <div class="c-margin-bottom--single">
                <h2 class="ai-assisted-heading">
                    <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" class="ai-sparkles-adjacent" />
                    AI Form Editor: {{ aiEditForm.ingestionPointName }}
                </h2>
                <button (click)="closeAiEditor()" class="aw-button aw-button--outline">← Back to Forms List</button>
            </div>
            <s25-ng-ai-form-editor
                [ingestionPointId]="aiEditForm.ingestionPointId"
                (exit)="closeAiEditor()"
                (buildComplete)="onAiEditComplete()"
            ></s25-ng-ai-form-editor>
        }

        @if (!editingForm && !showAiBuilder && !aiEditForm) {
            <div class="forms-admin-list-toolbar">
                <s25-ng-checkbox [(value)]="excludeDisabled" (valueChange)="onListFiltersChange()">
                    Exclude Disabled
                </s25-ng-checkbox>
                <s25-ng-checkbox [(value)]="includeDeleted" (valueChange)="onListFiltersChange()">
                    Show Deleted Forms
                </s25-ng-checkbox>
                <label>
                    Form Use:
                    <select
                        class="cn-form__control"
                        [(ngModel)]="selectedFormUseFilter"
                        (ngModelChange)="onListFiltersChange()"
                    >
                        <option value="">All Form Uses</option>
                        @for (formUseKey of INGESTION_POINT_FORM_USE_KEYS; track formUseKey) {
                            <option [value]="formUseKey">{{ INGESTION_POINT_FORM_USES[formUseKey] }}</option>
                        }
                    </select>
                </label>
                @if (canCreateForm()) {
                    <button (click)="addNew()" class="aw-button aw-button--outline">Create New Form</button>
                    @if (aiBuilderEnabled) {
                        <button (click)="openAiBuilder()" class="aw-button aw-button--outline ai-assisted-button">
                            <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" class="ai-sparkles-adjacent" />
                            AI Builder
                        </button>
                    }
                }
            </div>

            @if (dataSource) {
                <s25-ng-table
                    [caption]="'Forms'"
                    [unlimitedWidth]="true"
                    [columnSortable]="true"
                    [hasFilter]="true"
                    [dataSource]="dataSource"
                    [hasTotalRowCount]="true"
                    [hasRefresh]="true"
                    [showHeaderWhenNoData]="true"
                ></s25-ng-table>
            }
        }

        @if (editingForm && addingNew) {
            <div class="c-margin-bottom--double">
                <h2>Create New Form</h2>

                <div class="c-margin-bottom--single">
                    <label class="ngBlock">
                        Form Name:
                        <input
                            class="c-input"
                            type="text"
                            [(ngModel)]="editingForm.ingestionPointName"
                            placeholder="Enter form name"
                        />
                    </label>
                </div>

                <div class="c-margin-bottom--single">
                    <label class="ngBlock">
                        Description (optional):
                        <textarea
                            class="cn-form__control"
                            [(ngModel)]="editingForm.description"
                            placeholder="Enter a description for this form"
                            rows="3"
                        ></textarea>
                    </label>
                </div>

                @if (!categoryIn) {
                    <div class="c-margin-bottom--single">
                        <label class="ngBlock">
                            Category:&nbsp;
                            <select
                                class="cn-form__control"
                                [(ngModel)]="editingForm.category"
                                (ngModelChange)="onCategoryChange()"
                            >
                                @for (category of INGESTION_CATEGORIES; track category) {
                                    <option [value]="category">{{ category }}</option>
                                }
                            </select>
                        </label>
                    </div>
                } @else {
                    <div class="c-margin-bottom--single">Category: {{ editingForm.category }}</div>
                }

                @if (editingForm.category) {
                    <div class="c-margin-bottom--single">
                        <label class="ngBlock">
                            Sub-Category:
                            <s25-ng-ingestion-subcategory-selector
                                [category]="editingForm.category"
                                [subCategory]="editingForm.subCat"
                                [isEditing]="!subCategoryIn"
                                [allowClear]="!subCategoryIn"
                                [onSubCategoryChange]="onSubCategoryChange"
                            ></s25-ng-ingestion-subcategory-selector>
                        </label>
                    </div>
                }

                <div class="c-margin-bottom--single">
                    <div class="ngBlock" role="group" aria-labelledby="ingestion-new-form-use-label">
                        <span id="ingestion-new-form-use-label">Can be used for:</span>
                        <s25-ng-ingestion-form-use-help />
                        <div class="use-checkbox-group">
                            <s25-ng-checkbox [(value)]="editingForm.use!.registration"> Registration </s25-ng-checkbox>
                            <s25-ng-checkbox [(value)]="editingForm.use!.eventSurvey"> Event Survey </s25-ng-checkbox>
                            <s25-ng-checkbox [(value)]="editingForm.use!.eventCreation">
                                Event Creation
                            </s25-ng-checkbox>
                            <s25-ng-checkbox [(value)]="editingForm.use!.orgCreation"> Org Creation </s25-ng-checkbox>
                            <s25-ng-checkbox [(value)]="editingForm.use!.orgEdit"> Org Edit </s25-ng-checkbox>
                            <s25-ng-checkbox [(value)]="editingForm.use!.orgJoin"> Join Organization </s25-ng-checkbox>
                            <s25-ng-checkbox [(value)]="editingForm.use!.eventEdit">Event Edit</s25-ng-checkbox>
                            <s25-ng-checkbox [(value)]="editingForm.use!.electionCandidate">
                                Election candidate
                            </s25-ng-checkbox>
                        </div>
                    </div>
                </div>

                <div class="c-margin-top--single">
                    <s25-ng-button [type]="'primary'" [onClick]="saveNewForm">Create Form</s25-ng-button>
                    <button (click)="cancelEdit()" class="c-margin-left--single aw-button aw-button--danger--outline">
                        Cancel
                    </button>
                </div>
            </div>
        }

        @if (editingForm && !addingNew) {
            <div class="c-margin-bottom--single">
                <h2>Edit Form: {{ editingForm.ingestionPointName }}</h2>
                <button (click)="backToTable()" class="aw-button aw-button--outline">← Back to Forms List</button>
            </div>

            <s25-ng-admin-ingestion-form
                [ingestionPointId]="editingForm.ingestionPointId"
                (onSave)="onFormSaved()"
                (onCancel)="onFormCancelled()"
            ></s25-ng-admin-ingestion-form>
        }
    `,
    styles: `
        .form-header {
            border-bottom: 2px solid #007bff;
            padding-bottom: 10px;
            margin-bottom: 20px;
        }

        .form-section {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 5px;
            margin-bottom: 20px;
        }

        .use-checkbox-group {
            display: inline-flex;
            gap: 1rem;
            align-items: center;
        }

        .forms-admin-list-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem 1.25rem;
            margin-bottom: 1rem;
        }

        .ai-assisted-heading {
            display: flex;
            align-items: center;
            gap: 0.35em;
        }

        .ai-assisted-button {
            display: inline-flex;
            align-items: center;
            gap: 0.35em;
        }

        .ai-sparkles-adjacent {
            flex-shrink: 0;
        }

        :host ::ng-deep .s25-ng-table--table tbody td.forms-admin-workflow-cell s25-ng-button {
            --button--text-align: left;
        }

        :host ::ng-deep .s25-ng-table--table tbody td.forms-admin-workflow-cell s25-ng-button button.aw-button--link {
            font-size: 0.9rem !important;
            font-weight: 400 !important;
            line-height: inherit;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionAdminComponent implements OnInit {
    @ViewChild(S25TableComponent) table: S25TableComponent;

    @Input() categoryIn?: IngestionPointCategory;
    @Input() subCategoryIn?: string;

    editingForm: IngestionPoint | null = null;
    addingNew: boolean = false;
    showAiBuilder: boolean = false;
    aiEditForm: IngestionPoint | null = null;
    aiBuilderEnabled: boolean = false;
    dataSource: Table.Unpaginated;
    dateFormat: string;
    includeDeleted: boolean = false;
    excludeDisabled: boolean = false;
    selectedFormUseFilter: IngestionPointFormUseKey | "" = "";
    copyableFormIds: Set<number> = new Set();
    private lastOpenedNavigationFormId: number | undefined;
    INGESTION_CATEGORIES = INGESTION_CATEGORIES;
    INGESTION_POINT_FORM_USE_KEYS = INGESTION_POINT_FORM_USE_KEYS;
    INGESTION_POINT_FORM_USES = INGESTION_POINT_FORM_USES;

    // Security service - can be swapped out for different security frameworks
    securityService = inject(INGESTION_SECURITY_SERVICE);
    private itemService = inject(ItemService);

    constructor(public cd: ChangeDetectorRef) {}

    canCreateForm(): boolean {
        return this.securityService.canCreateForm(this.categoryIn, this.subCategoryIn);
    }

    canManageForm(form: IngestionPoint): boolean {
        return this.securityService.canManageForm(form);
    }

    canViewForm(form: IngestionPoint): boolean {
        return this.securityService.canViewForm(form);
    }

    async ngOnInit() {
        if (this.subCategoryIn && !this.categoryIn) {
            throw new Error("Sub-category provided without category");
        }

        onSeFormsActivated(() => {
            void this.openFormFromNavigation();
        });

        const result = await S25Util.all({
            dateFormat: UserprefService.getS25Dateformat(),
            prefs: PreferenceService.getPreferences(["config_ai_form_workflow_builder"], "S"),
            copyableFormIds: this.canCreateForm()
                ? Promise.resolve(new Set<number>())
                : loadCopyableIngestionPointIds(this.categoryIn, this.subCategoryIn),
        });
        this.dateFormat = result.dateFormat;
        this.aiBuilderEnabled = result.prefs?.config_ai_form_workflow_builder?.value === "T";
        this.copyableFormIds = result.copyableFormIds;
        this.initDataSource();
        this.cd.detectChanges();

        void this.openFormFromNavigation();
    }

    private async openFormFromNavigation(): Promise<void> {
        const ingestionPointId = S25Util.parseInt(String(StateService.getState()?.params?.ingestionPointId ?? ""));

        if (!ingestionPointId || this.lastOpenedNavigationFormId === ingestionPointId) {
            return;
        }

        try {
            const form = await DataIngestionService.getIngestionPoint(ingestionPointId);
            if (!form) {
                throw new Error("Cannot open form: copied form not found");
            }
            if (!this.canManageForm(form)) {
                S25Util.showError("You do not have permission to edit this form.");
                return;
            }

            this.editingForm = form;
            this.addingNew = false;
            this.lastOpenedNavigationFormId = ingestionPointId;
            this.cd.detectChanges();
        } catch (error) {
            S25Util.showError(error, "Failed to open copied form.");
        }
    }

    private initDataSource() {
        const columns: Table.Column[] = [
            { id: "ingestionPointId", header: "ID" },
            { id: "category", header: "Category" },
            { id: "subCat", header: "Sub-Category" },
            { id: "ingestionPointName", header: "Form Name" },
            { id: "createDate", header: "Created", type: "date" },
            { id: "lastModDate", header: "Last Modified", type: "date" },
            { id: "enabled", header: "Status" },
            { id: "use", header: "Form Use" },
            { id: "workflow", header: "Workflow", cells: { className: "forms-admin-workflow-cell" } },
            GenericTableButtonComponent.Column("Preview", this.previewForm, "outline"),
            GenericTableButtonComponent.Column("Download QR", this.downloadQrForm, "outline"),
        ];

        if (this.canCreateForm() || this.copyableFormIds.size > 0) {
            columns.push(
                GenericTableButtonComponent.Column("Copy", this.copyForm, "outline"),
                GenericTableButtonComponent.Column("Edit", this.edit, "outline"),
            );
        }

        if (this.canCreateForm()) {
            if (this.aiBuilderEnabled) {
                columns.push(
                    GenericTableButtonComponent.Column(
                        "AI Editor",
                        this.editViaAi,
                        "outline",
                        "min-content",
                        undefined,
                        undefined,
                        true,
                    ),
                );
            }
            columns.push(GenericTableButtonComponent.Column("Delete", this.delete, "danger--outline"));
        }

        this.dataSource = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: columns,
        };
    }

    previewForm = async (row: Table.Row, instance?: GenericTableButtonComponent) => {
        const form = row.data as IngestionPoint;
        if (!this.canViewForm(form)) {
            S25Util.showError("You do not have permission to preview this form.");
            return;
        }
        if (!form.ingestionPointId) {
            S25Util.showError("Cannot preview form: form ID is missing");
            return;
        }

        try {
            if (instance) {
                instance.isLoading = true;
            }
            const previewData = await loadSavedIngestionFormPreview(form.ingestionPointId, this.itemService);
            await ModalService.modal("ingestion-form-preview", {
                title: `Preview: ${previewData.ingestionPoint.ingestionPointName ?? "Form"}`,
                ...previewData,
            });
        } catch (error) {
            S25Util.showError(error, "Failed to load form preview.");
        } finally {
            if (instance) {
                instance.isLoading = false;
            }
        }
    };

    downloadQrForm = async (row: Table.Row) => {
        const form = row.data as IngestionPoint;
        if (!this.canViewForm(form)) {
            S25Util.showError("You do not have permission to download a QR code for this form.");
            return;
        }
        if (!form.ingestionPointId) {
            S25Util.showError("Cannot download QR code: form ID is missing");
            return;
        }
        await ModalService.modal("ingestion-form-qr", {
            title: `QR Code: ${form.ingestionPointName ?? "Form"}`,
            ingestionPointId: form.ingestionPointId,
            formName: form.ingestionPointName,
        });
    };

    edit = (row: Table.Row) => {
        const form = row.data as IngestionPoint;
        if (!this.canManageForm(form)) {
            S25Util.showError("You do not have permission to edit this form.");
            return;
        }
        this.editingForm = form;
        this.addingNew = false;
        this.cd.detectChanges();
    };

    openAiBuilder() {
        this.showAiBuilder = true;
        this.cd.detectChanges();
    }

    closeAiBuilder() {
        this.showAiBuilder = false;
        this.cd.detectChanges();
    }

    editViaAi = (row: Table.Row) => {
        const form = row.data as IngestionPoint;
        if (!this.canManageForm(form)) {
            S25Util.showError("You do not have permission to edit this form.");
            return;
        }
        this.aiEditForm = form;
        this.cd.detectChanges();
    };

    closeAiEditor() {
        this.aiEditForm = null;
        this.cd.detectChanges();
    }

    onAiEditComplete = async () => {
        this.aiEditForm = null;
        this.cd.detectChanges();
        await this.table?.refresh(true, true);
    };

    addNew = () => {
        this.editingForm = {
            ingestionPointName: "",
            category: this.categoryIn,
            subCat: this.subCategoryIn || "",
            description: "",
            enabled: false,
            allowEditAfterSubmit: false,
            use: {},
        };

        // Auto-set category if only one option available and no input provided
        if (!this.categoryIn && INGESTION_CATEGORIES.length === 1) {
            this.editingForm.category = INGESTION_CATEGORIES[0];
        }

        this.addingNew = true;
        this.cd.detectChanges();
    };

    copyForm = async (row: Table.Row, instance: GenericTableButtonComponent) => {
        const sourcePoint = row.data as IngestionPoint;

        if (!sourcePoint.ingestionPointId) {
            S25Util.showError("Cannot copy form: form ID is missing");
            return;
        }

        if (!canCopyIngestionPoint(sourcePoint, this.copyableFormIds, this.canCreateForm())) {
            S25Util.showError("You do not have permission to copy this form.");
            return;
        }

        try {
            const newForm = await copyIngestionForm(sourcePoint.ingestionPointId, {
                targetSubCat: this.subCategoryIn,
            });

            this.editingForm = newForm;
            this.addingNew = false;
            await this.table?.refresh(true, true);
            this.cd.detectChanges();
        } catch (error) {
            S25Util.showError(error);
        }
    };

    delete = async (row: Table.Row, instance: GenericTableButtonComponent) => {
        const point = row.data as IngestionPoint;

        if (!this.canManageForm(point)) {
            S25Util.showError("You do not have permission to delete this form.");
            return;
        }

        const confirmed = await AlertService.confirm(
            `<pre>Are you absolutely sure you want to deactivate this form (${point.ingestionPointName})?\n\n` +
                `WARNING: This will:\n` +
                `• Prevent further submissions to this form\n` +
                `• Hide the form from users (it will no longer appear in form lists)\n` +
                `• Preserve all existing form data and submissions\n` +
                `• Preserve all form fields and configuration\n\n` +
                `The form can be reactivated later if needed.</pre>`,
        );

        if (!confirmed) return;

        if (!point.ingestionPointId) {
            S25Util.showError("Cannot deactivate form: form ID is missing");
            return;
        }

        try {
            await DataIngestionService.deleteIngestionPoint(point.ingestionPointId);
            await this.table?.refresh(true, true);
            this.cd.detectChanges();
        } catch (error) {
            S25Util.showError("Failed to deactivate form: " + error);
        }
    };

    cancelEdit = () => {
        this.editingForm = null;
        this.addingNew = false;
        this.cd.detectChanges();
    };

    backToTable = () => {
        this.editingForm = null;
        this.lastOpenedNavigationFormId = undefined;
        void StateService.go("seForms", { ingestionPointId: "" }, { location: "replace" });
        this.cd.detectChanges();
    };

    saveNewForm = async () => {
        if (!this.editingForm?.ingestionPointName?.trim()) {
            S25Util.showError("Form name is required");
            return;
        }

        if (!this.editingForm.category) {
            S25Util.showError("Category is required");
            return;
        }

        if (!this.canCreateForm()) {
            S25Util.showError("You do not have permission to create forms.");
            return;
        }

        // For ENGAGEMENT forms, ensure orgId is set correctly
        if (this.editingForm.category === IngestionPointCategory.ENGAGEMENT && this.subCategoryIn) {
            this.editingForm.subCat = this.subCategoryIn;
        }

        try {
            const [resp, error] = await S25Util.Maybe(DataIngestionService.createIngestionPoint(this.editingForm));
            if (error) {
                S25Util.showError(error);
                return;
            }

            // Enter edit mode for the newly created form instead of going back to list
            this.editingForm = resp;
            this.addingNew = false;
            await this.table?.refresh(true, true);
            this.cd.detectChanges();
        } catch (error) {
            S25Util.showError("Failed to create form: " + error);
        }
    };

    onFormSaved = async () => {
        // Refresh the table and go back to the main view
        await this.table?.refresh(true, true);
        this.backToTable();
    };

    onFormCancelled = () => {
        this.backToTable();
    };

    onCategoryChange = () => {
        if (this.editingForm) {
            this.editingForm.subCat = "";
        }
        this.cd.detectChanges();
    };

    onSubCategoryChange = (subCategory: string) => {
        if (this.editingForm) {
            this.editingForm.subCat = subCategory;
        }
        this.cd.detectChanges();
    };

    onListFiltersChange = async () => {
        if (this.table) {
            await this.table.refresh(true, true);
        }
        this.cd.detectChanges();
    };

    openDirectWorkflowEditor = async (workflowProcessId: number): Promise<void> => {
        await StateService.go("seWorkflow", { workflowProcessId: String(workflowProcessId) }, null);
    };

    getRows = async () => {
        const rows: Table.Row[] = [];
        const formUseFilter = this.selectedFormUseFilter ? [this.selectedFormUseFilter] : undefined;
        const fetchedPoints =
            (await DataIngestionService.getIngestionPoints(
                this.categoryIn,
                this.subCategoryIn,
                this.includeDeleted,
                formUseFilter,
            )) ?? [];
        const points = filterIngestionPointsForList(fetchedPoints, this.excludeDisabled);
        const workflowNameById = buildWorkflowNameById(await WorkflowService.getWorkflowProcesses());

        if (points.length) {
            // Populate subCategoryNames for ENGAGEMENT category
            await DataIngestionService.populateSubCategoryNames(IngestionPointCategory.ENGAGEMENT, points);

            jSith.forEach(points, (idx, point: IngestionPoint) => {
                if (point.ingestionPointId == null) {
                    return;
                }

                const cells: Table.Row["cells"] = {};
                for (let i = 0; i < this.dataSource.columns.length; i++) {
                    const column = this.dataSource.columns[i];
                    const cell: Cell = {};

                    const pointValue = point[column.id as keyof IngestionPoint] as string | undefined;
                    if (column.type === "date") {
                        cell.text = S25Datefilter.transform(pointValue, this.dateFormat);
                        cell.sortValue = pointValue || "";
                    } else if (column.id === "ingestionPointId") {
                        const ingestionPointId = point.ingestionPointId;
                        cell.text = ingestionPointId != null ? String(ingestionPointId) : "";
                        cell.sortValue = ingestionPointId != null ? String(ingestionPointId).padStart(12, "0") : "";
                    } else if (column.id === "enabled") {
                        cell.text = pointValue ? "Enabled" : "Disabled";
                        cell.sortValue = pointValue ? "1" : "0";
                    } else if (column.id === "subCat") {
                        // Use subCategoryName if available, otherwise fall back to subCat
                        cell.text = point.subCategoryName || pointValue || "";
                    } else if (column.id === "use") {
                        cell.text = formatFormUseColumnText(point.use);
                    } else if (column.id === "workflow") {
                        const workflowProcessId = point.workflowId;
                        const workflowLabel = getDirectWorkflowDisplayName(workflowProcessId, workflowNameById);
                        if (workflowProcessId && workflowLabel) {
                            cell.component = GenericTableButtonComponent;
                            cell.inputs = {
                                label: workflowLabel,
                                type: "link",
                                onClick: () => this.openDirectWorkflowEditor(workflowProcessId),
                            } satisfies Table.Cell<GenericTableButtonComponent>["inputs"];
                            cell.textValue = workflowLabel;
                            cell.sortValue = workflowLabel;
                        } else {
                            cell.text = "";
                            cell.sortValue = "";
                        }
                    } else if (column.id === "preview") {
                        const rowCanView = this.canViewForm(point);
                        cell.component = GenericTableButtonComponent;
                        cell.inputs = {
                            label: "Preview",
                            type: "outline",
                            disabled: !rowCanView,
                            disabledReason: rowCanView ? undefined : "You do not have permission to preview this form.",
                            onClick: column.cells?.inputs?.onClick,
                        } satisfies Table.Cell<GenericTableButtonComponent>["inputs"];
                    } else if (column.id === "download_qr") {
                        const rowCanView = this.canViewForm(point);
                        cell.component = GenericTableButtonComponent;
                        cell.inputs = {
                            label: "Download QR",
                            type: "outline",
                            disabled: !rowCanView,
                            disabledReason: rowCanView
                                ? undefined
                                : "You do not have permission to download a QR code for this form.",
                            onClick: column.cells?.inputs?.onClick,
                        } satisfies Table.Cell<GenericTableButtonComponent>["inputs"];
                    } else if (column.id === "copy") {
                        const rowCanCopy = canCopyIngestionPoint(point, this.copyableFormIds, this.canCreateForm());
                        cell.component = GenericTableButtonComponent;
                        cell.inputs = {
                            label: "Copy",
                            type: "outline",
                            disabled: !rowCanCopy,
                            disabledReason: rowCanCopy ? undefined : "You do not have permission to copy this form.",
                            onClick: column.cells?.inputs?.onClick,
                        } satisfies Table.Cell<GenericTableButtonComponent>["inputs"];
                    } else if (column.id === "edit") {
                        const rowCanManage = this.canManageForm(point);
                        cell.component = GenericTableButtonComponent;
                        cell.inputs = {
                            label: "Edit",
                            type: "outline",
                            disabled: !rowCanManage,
                            disabledReason: rowCanManage ? undefined : "You do not have permission to edit this form.",
                            onClick: column.cells?.inputs?.onClick,
                        } satisfies Table.Cell<GenericTableButtonComponent>["inputs"];
                    } else {
                        cell.text = pointValue || "";
                    }
                    cells[column.id] = cell;
                }

                rows.push({
                    id: point.ingestionPointId,
                    name: point.ingestionPointId + "",
                    cells: cells,
                    data: point,
                });
            });
        }

        return {
            rows: rows,
            totalRows: points.length,
        };
    };
}
